import { OvertimeButtons } from "components/OvertimeButtons";
import React from "react";
import { useCreateOvertimeMutation, useUpdateOvertimeMutation } from "store/reducers/overtime";

import { Duration } from "./table_components/duration";
import { Button } from "../components/button";
import { Modal, ModalProps } from "../components/modal";
import { TextArea } from "../components/textarea";

type Props = {
  day: string;
  employeeId: number;
  title: string;
  overtimeDuration: number | null;
  overtime?: Overtime;
} & ModalProps;

export function OvertimeNoteModal(props: Props) {
  const { day, dismissModal, employeeId, title, overtimeDuration, overtime, ...reactModalProps } = props;
  const [note, setNote] = React.useState("");

  if (overtimeDuration === null && !overtime) {
    // We don't render this component if there is no overtime duration or overtime
    throw new Error("Overtime duration is null");
  }

  const [createOvertime] = useCreateOvertimeMutation();
  const [updateOvertime] = useUpdateOvertimeMutation();

  React.useEffect(() => {
    if (overtime?.reason) {
      setNote(overtime.reason);
    }
  }, [overtime?.reason]);

  function applyOvertime() {
    if (!overtime) {
      createOvertime({
        employee_id: employeeId,
        when: day,
        reason: note,
      })
        .unwrap()
        .then(() => {
          dismissModal();
        })
        .catch((error) => {
          console.error("Error applying overtime", error);
        });
    } else {
      updateOvertime({
        id: overtime.id,
        reason: note,
        employee_id: overtime.employee_id,
        when: overtime.when,
      })
        .unwrap()
        .then(() => {
          dismissModal();
        })
        .catch((error) => {
          console.error("Error updating overtime", error);
        });
    }
  }

  return (
    <Modal
      {...reactModalProps}
      dismissModal={dismissModal}
      title={title}
      buttons={
        <Button colour="accent" onClick={() => applyOvertime()}>
          Submit
        </Button>
      }
    >
      <div className="w-full rounded-lg bg-white">
        {overtimeDuration !== null && (
          <div className="mb-4 flex flex-row gap-1">
            Applying for
            <Duration seconds={overtimeDuration} /> overtime
          </div>
        )}

        <div className="md:flex mb-2">
          <div className="md:w-full">
            <label className="block uppercase tracking-wide text-grey-darker text-xs font-bold mb-2">
              Please provide a reason for your overtime work
            </label>
            <TextArea
              name="Notes"
              placeholder="Reason..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mb-1"
              autoFocus
            />
          </div>
        </div>
        <OvertimeButtons
          date={day}
          timesheetEmployeeId={employeeId}
          currentEmployeeId={undefined}
          overtime={overtime}
          parent="MODAL"
          overtimeDuration={overtimeDuration}
        />
      </div>
    </Modal>
  );
}
